import { useNotifications } from '@components/Notifications';
import { MEDIA_LIST_BASE_KEY } from '@media-lists/hooks/mediaListsQueries';
import {
  ContactDuplicateGroup,
  ContactDuplicateId,
  DuplicatedContactGroup,
} from '@src/types/duplicateContacts';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DuplicateContactsModal } from './DuplicateContactsModal';
import { resolveDuplicatedContacts } from './resolveDuplicatedContacts';

interface OpenDuplicateContactsModalParams {
  duplicatedContacts: DuplicatedContactGroup[];
  targetMediaListsId: number[];
  addedContactIds?: number[];
  onResolved: () => void | Promise<void>;
  onCancel?: () => void;
}

interface RequestDuplicateContactsSelectionParams {
  duplicatedContacts: ContactDuplicateGroup[];
  addedContactIds?: ContactDuplicateId[];
}

type DuplicateContactsModalState =
  | { mode: 'resolveAfterAdding'; params: OpenDuplicateContactsModalParams }
  | { mode: 'selectBeforeAdding'; params: RequestDuplicateContactsSelectionParams };

interface DuplicateContactsModalContextValue {
  openDuplicateContactsModal: (params: OpenDuplicateContactsModalParams) => void;
  requestDuplicateContactsSelection: (
    params: RequestDuplicateContactsSelectionParams,
  ) => Promise<Set<ContactDuplicateId> | null>;
}

const DuplicateContactsModalContext = createContext<DuplicateContactsModalContextValue | null>(
  null,
);

export const DuplicateContactsModalProvider = ({ children }: React.PropsWithChildren) => {
  const { t } = useTranslation(['mediaDatabase']);
  const { showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();
  const [modalState, setModalState] = useState<DuplicateContactsModalState | null>(null);
  const isResolvingRef = useRef(false);
  const selectionResolverRef = useRef<
    ((selectedContactIds: Set<ContactDuplicateId> | null) => void) | null
  >(null);

  const openDuplicateContactsModal = (params: OpenDuplicateContactsModalParams) => {
    setModalState({ mode: 'resolveAfterAdding', params });
  };

  const requestDuplicateContactsSelection = (params: RequestDuplicateContactsSelectionParams) =>
    new Promise<Set<ContactDuplicateId> | null>((resolve) => {
      selectionResolverRef.current = resolve;
      setModalState({ mode: 'selectBeforeAdding', params });
    });

  const settleSelection = (selectedContactIds: Set<ContactDuplicateId> | null) => {
    selectionResolverRef.current?.(selectedContactIds);
    selectionResolverRef.current = null;
  };

  const closeModal = () => {
    if (!modalState) {
      return;
    }

    if (modalState.mode === 'selectBeforeAdding') {
      settleSelection(null);
    } else {
      modalState.params.onCancel?.();
    }

    setModalState(null);
  };

  const handleConfirm = async (selectedContactIds: Set<ContactDuplicateId>) => {
    if (!modalState || isResolvingRef.current) {
      return;
    }

    if (modalState.mode === 'selectBeforeAdding') {
      setModalState(null);
      settleSelection(selectedContactIds);

      return;
    }

    const { duplicatedContacts, targetMediaListsId, onResolved } = modalState.params;

    isResolvingRef.current = true;
    setModalState(null);

    try {
      await resolveDuplicatedContacts(duplicatedContacts, selectedContactIds, targetMediaListsId);
      await onResolved();
    } catch {
      await queryClient.invalidateQueries({ queryKey: [MEDIA_LIST_BASE_KEY] });
      showErrorNotification(t('mediaDatabase:duplicateContactsModal.failedToResolve'));
    } finally {
      isResolvingRef.current = false;
    }
  };

  return (
    <DuplicateContactsModalContext.Provider
      value={{ openDuplicateContactsModal, requestDuplicateContactsSelection }}>
      {children}
      {modalState && (
        <DuplicateContactsModal
          isVisible
          addedContactIds={modalState.params.addedContactIds}
          duplicatedContacts={modalState.params.duplicatedContacts}
          onClose={closeModal}
          onConfirm={handleConfirm}
        />
      )}
    </DuplicateContactsModalContext.Provider>
  );
};

export const useDuplicateContactsModal = () => {
  const context = useContext(DuplicateContactsModalContext);

  if (!context) {
    throw new Error('useDuplicateContactsModal must be used within DuplicateContactsModalProvider');
  }

  return context;
};
